import qs from 'query-string';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import type { SortState } from './types';

/** The slice of list state persisted to the URL (page/pageSize are intentionally transient). */
export type PersistedListState<TFilters> = {
  search?: string;
  sort?: SortState;
  filters?: Partial<TFilters>;
};

/** A `Select` emits a bare id; a caller may pass a `{ id }` option instead. The id is what's kept. */
const selectedId = (value: unknown): unknown =>
  typeof value === 'object' && value !== null && 'id' in value ? value.id : value;

/** Nothing selected: the filter is left off the URL. `false` and `0` are values, so they're kept. */
const isCleared = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const stringParam = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/**
 * A filter value as a URL param. A string stays as typed, unless it would read back as another
 * type (`'12'`, `'true'`), when it is quoted; any other value is JSON. So `12` and `true` read back
 * as a number and a boolean, and `'12'` as a string.
 */
const encodeValue = (value: unknown): string =>
  typeof value === 'string' && parseJson(value) === undefined ? value : JSON.stringify(value);

const decodeValue = (raw: string): unknown => {
  const parsed = parseJson(raw);
  return parsed === undefined ? raw : parsed;
};

/**
 * Reads a list's state from the URL: `searchValue`, `sortCol` / `sortDir`, and each declared
 * filter under its own name, as written (an option as its `id`, `12` as a number). A missing or
 * empty param keeps the initial value.
 */
const readListState = <TFilters>(
  locationSearch: string,
  filterKeys: string[],
  initialSort: SortState
): PersistedListState<TFilters> => {
  const query = qs.parse(locationSearch);
  const field = stringParam(query.sortCol) ?? initialSort?.field;
  const direction = stringParam(query.sortDir) ?? initialSort?.direction;
  const filters = Object.fromEntries(
    filterKeys.flatMap(key => {
      const raw = query[key];
      if (!raw) return [];
      return [
        [key, Array.isArray(raw) ? raw.map(item => decodeValue(item ?? '')) : decodeValue(raw)],
      ];
    })
  );
  return {
    search: stringParam(query.searchValue),
    sort: field ? { field, direction: direction === 'desc' ? 'desc' : 'asc' } : undefined,
    // The URL carries untyped values; the query is what checks them.
    filters: filters as Partial<TFilters>,
  };
};

/**
 * Writes a list's state into the query string, keeping every other param: `searchValue` (even when
 * empty), `sortCol`, `sortDir`, and each declared filter that has a value. A cleared filter is
 * left off.
 */
const writeListState = <TFilters extends object>(
  locationSearch: string,
  { search = '', sort = null, filters = {} }: PersistedListState<TFilters>,
  filterKeys: string[]
): string => {
  const owned = new Set(['sortDir', 'sortCol', 'searchValue', ...filterKeys]);
  const query: Record<string, unknown> = Object.fromEntries(
    Object.entries(qs.parse(locationSearch)).filter(([key]) => !owned.has(key))
  );
  query.sortDir = sort?.direction;
  query.sortCol = sort?.field;
  query.searchValue = search;
  const values = Object.fromEntries(Object.entries(filters));
  filterKeys.forEach(key => {
    const stored = selectedId(values[key]);
    if (!isCleared(stored)) query[key] = encodeValue(stored);
  });
  return qs.stringify(query);
};

/**
 * Isolates URL persistence for a list. The URL reads and writes the filters declared in
 * `initialFilters`. When the list persists, the seed is read once on mount and `write` mirrors the
 * given state back to the URL; otherwise neither touches the URL.
 */
export const useListPersistence = <TFilters extends object>(
  persist: boolean,
  initialFilters: TFilters,
  initialSort: SortState
): {
  seed: PersistedListState<TFilters>;
  write: (state: PersistedListState<TFilters>) => void;
} => {
  const history = useHistory();

  // Read once on mount — deep-link/refresh restores; later navigations must not re-seed.
  const { filterKeys, seed } = useMemo(() => {
    const keys = Object.keys(initialFilters);
    return {
      filterKeys: keys,
      seed: persist ? readListState<TFilters>(history.location.search, keys, initialSort) : {},
    };
  }, []);

  const write = useCallback(
    (state: PersistedListState<TFilters>) => {
      if (!persist) return;
      history.replace({ search: writeListState(history.location.search, state, filterKeys) });
    },
    [history, persist, filterKeys]
  );

  return { seed, write };
};
